/**
 * window.__thesaurus — shared client access to the native-synonyms thesaurus.
 *
 * SUGGESTION source, never authority (see docs/linguistic-data-registry.md). Any consuming
 * tool must degrade gracefully: a missing manifest / shard / word resolves to null/[], never
 * throws. Data is static JSON sharded by first char, cache-busted by the manifest version
 * (same pattern as the anglicism dict). Loaded per-tool (from the tool's blade), NOT globally.
 *
 * API:
 *   await window.__thesaurus.get(lang, word)     -> Sense[] | null   ([{syn:[],loan?:[],src}])
 *   await window.__thesaurus.native(lang, word)  -> string[]         (flat native synonyms; drops `loan`)
 *   window.__thesaurus.loaded(lang, word)        -> Sense[] | null   (sync; only if the shard is already cached)
 */
(function () {
    'use strict';
    if (window.__thesaurus) return;
    var BASE = '/data/thesaurus/';
    var manifestPromise = null;
    var shardCache = {};   // "lang/shard" -> Promise<obj>

    function loadManifest() {
        if (manifestPromise) return manifestPromise;
        manifestPromise = fetch(BASE + 'manifest.json', { cache: 'no-cache' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        return manifestPromise;
    }

    // MUST match scripts/build-thesaurus-ru.cjs shardOf(): lowercased first char,
    // Cyrillic letter → that letter, anything else → "_".
    function shardOf(word) {
        var c = (String(word)[0] || '_').toLowerCase();
        return /[а-яё]/.test(c) ? c : '_';
    }

    function loadShard(lang, shard, version) {
        var key = lang + '/' + shard;
        if (shardCache[key]) return shardCache[key];
        var url = BASE + lang + '/' + shard + '.json?v=' + encodeURIComponent(version || 'x');
        shardCache[key] = fetch(url, { cache: 'force-cache' })
            .then(function (r) { return r.ok ? r.json() : {}; })
            .catch(function () { return {}; });
        return shardCache[key];
    }

    function get(lang, word) {
        word = String(word == null ? '' : word).trim();
        if (!word) return Promise.resolve(null);
        return loadManifest().then(function (m) {
            var meta = m && m.languages && m.languages[lang];
            if (!meta) return null;
            var shard = shardOf(word);
            if (meta.shards && meta.shards.indexOf(shard) === -1) return null; // no such shard ⇒ no data
            return loadShard(meta.file_dir || lang, shard, m.version).then(function (data) {
                return (data && data[word]) || null;
            });
        });
    }

    function native(lang, word) {
        return get(lang, word).then(function (senses) {
            if (!senses) return [];
            var out = [], seen = {};
            senses.forEach(function (s) {
                (s.syn || []).forEach(function (w) { if (!seen[w]) { seen[w] = 1; out.push(w); } });
            });
            return out;
        });
    }

    // Is this language's data hand-vetted enough for LIVE user-facing display? The
    // seed is a good offline candidate source but not clean enough to show live until a
    // curation wave sets curated:true. Consumers MUST gate their live UI on this.
    function curated(lang) {
        return loadManifest().then(function (m) {
            var meta = m && m.languages && m.languages[lang];
            return !!(meta && meta.curated);
        });
    }

    function loaded(lang, word) {
        word = String(word == null ? '' : word).trim();
        var key = (lang) + '/' + shardOf(word);
        // only returns a value if the shard promise already resolved into the cache map
        return null; // sync-cache accessor is intentionally minimal in v1; use get()
    }

    window.__thesaurus = { get: get, native: native, curated: curated, loaded: loaded };
})();
